/**
 * The Sketch's side panel: production (with and without shade), shading (per-face solar access, the months, the
 * heatmap and the sun time of the shadows), the selected face, line, tree, obstacle or neighbour, the panels, and
 * "Design with AI". Presentational sections; the editor passes state and callbacks. Styling: sketch.css (`.sk-side…`).
 */
import { Box, BrickWall, Building2, Check, CloudSun, Cylinder, Grid3x3, House, Info, Leaf, Magnet, Mountain, Move3d, RectangleHorizontal, RectangleVertical, RotateCcw, RotateCw, Sparkles, SunDim, Trash2, TreeDeciduous, TreePine, X } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys";
import type { Orientation } from "../editor/snap.ts";
import { PITCH_CHIPS, STOREY_CHIPS } from "./roofModel.ts";
import type { ObstacleItem } from "../editor/model.ts";
import { crownBase, heatColor, HEAT_STOPS, OBSTACLE_PRESETS, shapeOf, type ShadeItem } from "./shade.ts";
import type { EdgeType, Sketch, Slope } from "./skeleton.ts";
import { FACE_HUE, pitchRise } from "./SketchCanvas";
import { LINE_TYPES } from "./SketchToolbar";
import type { PlaceMode } from "./gizmo.ts";
import type { SketchSelection, SunTime } from "./types.ts";
import { noPanelNumbers } from "./state.ts";
import { compassName, faceName, type Text } from "./text.ts";
import { kbd, Msg, useSketchText } from "./useText.ts";

/** Units and key names (never translated). */
const UNIT_YIELD = "kWh / kWp";
const KEYS_SELECT_ALL = "⌘A";

/** "Jun" for a month index (0 = January), in the reader's language. */
const monthName = (tx: Text, month: number, style: "short" | "narrow" = "short") => tx.date(new Date(2026, month, 21), { month: style });

/** A number that glides to its new value (production updates feel alive, not jumpy). */
function useTween(target: number | null | undefined, ms = 550): number | null {
  const [shown, setShown] = useState<number | null>(target ?? null);
  const from = useRef<number | null>(target ?? null);
  useEffect(() => {
    if (target == null) return setShown(null);
    const start = from.current ?? 0;
    if (start === target || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      from.current = target;
      return setShown(target);
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      const e = 1 - Math.pow(1 - k, 3);
      const v = start + (target - start) * e;
      from.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return shown;
}

export function ProductionSection({ numbers: given, weather, pricing, panels, shading }: {
  numbers: S["EditorNumbers"];
  weather: S["WeatherSource"] | null | undefined;
  pricing: boolean;
  panels: number;
  shading?: S["SketchShading"] | null;
}) {
  // no panels on the faces as drawn: zero, whatever an earlier preview said
  const tx = useSketchText();
  const numbers = panels ? given : noPanelNumbers(given);
  const yieldPerKw = numbers.annual_kwh && numbers.kw_dc ? numbers.annual_kwh / numbers.kw_dc : null;
  const kw = useTween(numbers.kw_dc ?? 0);
  const kwh = useTween(numbers.annual_kwh ?? null);
  const price = useTween(numbers.price ?? null);
  return (
    <section className="sk-side-section sk-production" aria-live="polite">
      <div className={`sk-hero${pricing ? " is-busy" : ""}`}>
        <div className="sk-hero-label">{tx.t("sketch.production.systemSize")}</div>
        <div className="sk-hero-value">
          {tx.num(kw ?? 0, 2)}
          <small>kW</small>
        </div>
        <div className="sk-hero-sub">
          {tx.t(pricing ? "sketch.production.panelsUpdating" : "sketch.production.panels", { count: panels })}
        </div>
      </div>
      <dl className="sk-figures">
        <div>
          <dt>{tx.t("sketch.production.energy")}</dt>
          <dd title={numbers.annual_kwh != null ? tx.t("sketch.production.kwhYear", { kwh: tx.num(numbers.annual_kwh, 0) }) : undefined}>
            {kwh != null ? tx.num(kwh, 0) : "—"}
            <small>{tx.t("sketch.production.kwhPerYear")}</small>
          </dd>
        </div>
        <div>
          <dt>{tx.t("sketch.production.yield")}</dt>
          <dd>
            {yieldPerKw ? tx.num(yieldPerKw, 0) : "—"}
            <small>{UNIT_YIELD}</small>
          </dd>
        </div>
        <div>
          <dt>{tx.t("sketch.production.price")}</dt>
          <dd>
            {price != null ? tx.amount(Math.round(price), numbers.currency ?? "USD") : "—"}
            <small>{numbers.offset_pct != null ? tx.t("sketch.production.offset", { pct: tx.pct(numbers.offset_pct) }) : tx.t("sketch.production.fullSystem")}</small>
          </dd>
        </div>
      </dl>
      {numbers.unshaded_annual_kwh != null && numbers.shading_loss_pct != null && numbers.annual_kwh != null && (
        <div className="sk-shade-energy" aria-label={tx.t("sketch.production.shadeEnergy")}>
          <div>
            <span>{tx.t("sketch.production.withShade")}</span>
            <b>{tx.num(numbers.annual_kwh, 0)} <small>kWh</small></b>
          </div>
          <div>
            <span>{tx.t("sketch.production.withoutShade")}</span>
            <b>{tx.num(numbers.unshaded_annual_kwh, 0)} <small>kWh</small></b>
          </div>
          <div className={`sk-shade-loss${numbers.shading_loss_pct >= 10 ? " is-high" : numbers.shading_loss_pct >= 3 ? " is-mid" : ""}`}>
            <span>{tx.t("sketch.production.shadeLoss")}</span>
            <b>{tx.pct(numbers.shading_loss_pct, numbers.shading_loss_pct < 10 ? 1 : 0)}</b>
          </div>
          <i className="sk-shade-bar" aria-hidden><i style={{ width: `${Math.max(0, 100 - numbers.shading_loss_pct)}%` }} /></i>
        </div>
      )}
      {shading && shading.horizon.provisional && (
        <p className="sk-weather is-provisional">
          <Mountain size={14} />
          <span>{tx.t("sketch.production.noHorizon")}</span>
        </p>
      )}
      {weather && (
        <p className={`sk-weather${weather.provisional ? " is-provisional" : ""}`}>
          {weather.provisional ? <Info size={14} /> : <CloudSun size={14} />}
          <span>
            {weather.provisional
              ? tx.t("sketch.production.weatherProvisional")
              : <>{weather.years ? tx.t("sketch.production.weatherYears", { dataset: weather.dataset, years: weather.years }) : tx.t("sketch.production.weatherYear", { dataset: weather.dataset })} {weather.credit ?? ""}</>}
          </span>
        </p>
      )}
    </section>
  );
}

export function RoofSection(props: {
  sketch: Sketch;
  slopes: Record<string, Slope>;
  selection: SketchSelection;
  defaults: { pitch: number; eaveHeight: number };
  readOnly: boolean;
  onDefaults: (d: { pitch?: number; eaveHeight?: number }) => void;
  onFace: (id: string, change: { pitch?: number; eaveHeight?: number; azimuth?: number }) => void;
  onEdgeType: (id: string, type: EdgeType) => void;
  onDeleteEdge: (id: string) => void;
}) {
  const tx = useSketchText();
  const sel = props.selection;
  const face = sel?.kind === "face" ? props.sketch.faces.find((f) => f.id === sel.id) : null;
  const edge = sel?.kind === "edge" ? props.sketch.edges.find((e) => e.id === sel.id) : null;
  const index = face ? props.sketch.faces.indexOf(face) : -1;
  const slope = face ? props.slopes[face.id] : null;
  const pitch = face ? face.pitch : props.defaults.pitch;
  const height = face ? face.eaveHeight : props.defaults.eaveHeight;
  const set = (change: { pitch?: number; eaveHeight?: number }) => (face ? props.onFace(face.id, change) : props.onDefaults(change));
  if (edge) {
    const a = props.sketch.vertices[edge.a];
    const b = props.sketch.vertices[edge.b];
    const length = a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0;
    return (
      <section className="sk-side-section sk-roof" key={`edge-${edge.id}`}>
        <div className="sk-side-head">
          <span className="sk-side-icon"><i className={`sk-swatch sk-swatch-${edge.type}`} style={{ width: 16 }} /></span>
          <h3>{tx.t("sketch.roof.line")}</h3>
          <span className="sk-eyebrow">{tx.t("sketch.unit.m", { value: tx.num(length, 2) })}</span>
        </div>
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.toolsec.type")}</span>
          <div className="sk-chips" role="radiogroup" aria-label={tx.t("sketch.toolsec.lineType")}>
            {LINE_TYPES.map(([type, label]) => (
              <button key={type} type="button" role="radio" aria-checked={edge.type === type} className={`sk-chip${edge.type === type ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onEdgeType(edge.id, type)}>
                <i className={`sk-swatch sk-swatch-${type}`} style={{ width: 14 }} /> {tx.t(label)}
              </button>
            ))}
          </div>
        </div>
        <div className="sk-actions">
          <button type="button" className="sk-action is-danger" disabled={props.readOnly} onClick={() => props.onDeleteEdge(edge.id)}>
            <Trash2 size={14} /> {tx.t("sketch.roof.deleteLine")}
          </button>
        </div>
      </section>
    );
  }
  const az = face ? (slope?.eave ? slope.azimuth : face.azimuth) : null;
  return (
    <section className="sk-side-section sk-roof" key={face ? `face-${face.id}` : "roof"}>
      <div className="sk-side-head">
        {face ? (
          <span className="sk-face-swatch" style={{ "--sk-face-hue": FACE_HUE[index % FACE_HUE.length] } as CSSProperties}>{index + 1}</span>
        ) : (
          <span className="sk-side-icon"><House size={16} /></span>
        )}
        <h3>{face ? faceName(tx, face.label, index) : tx.t("sketch.roof.roof")}</h3>
        <span className="sk-eyebrow">{tx.t(face ? "sketch.roof.selectedFace" : "sketch.roof.newFaces")}</span>
      </div>
      {face && az != null ? (
        <div className="sk-compass">
          <svg viewBox="0 0 64 64" aria-hidden>
            <circle cx="32" cy="32" r="29" className="sk-compass-dial" />
            <path className="sk-compass-ticks" d="M32 5v5M32 54v5M5 32h5M54 32h5" />
            <text x="32" y="17" className="sk-compass-n">{tx.t("sketch.compass.n")}</text>
            <path className="sk-compass-needle" style={{ transform: `rotate(${az}deg)` }} d="M32 12 L38 34 L32 30 L26 34 Z" />
            <circle cx="32" cy="32" r="3" fill="var(--sk-text)" />
          </svg>
          <div>
            <b>{tx.t("sketch.roof.faces", { dir: compassName(tx, az), deg: tx.num(Math.round(az), 0) })}</b>
            <span>{tx.t(slope?.eave ? "sketch.roof.downSlope" : "sketch.roof.noEaveBelow")}</span>
          </div>
        </div>
      ) : (
        <p className="sk-caption">{tx.t(face ? "sketch.roof.noEaveHere" : "sketch.roof.defaultsCaption")}</p>
      )}
      <div className="sk-field">
        <span className="sk-label">
          {tx.t("sketch.roof.pitch")} <span>{pitch > 0.5 ? tx.t("sketch.pitch.riseAndDegrees", { rise: tx.t("sketch.pitch.rise", { rise: tx.upTo(pitchRise(pitch), 1) }), deg: tx.num(pitch, 1) }) : tx.t("sketch.roof.degrees", { deg: tx.num(pitch, 1) })}</span>
        </span>
        <div className="sk-pitch-grid" role="radiogroup" aria-label={tx.t("sketch.roof.pitch")}>
          {PITCH_CHIPS.map(([rise, deg]) => {
            const label = rise ? tx.t("sketch.pitch.rise", { rise }) : tx.t("sketch.pitch.flat");
            return (
              <button key={rise} type="button" role="radio" aria-checked={Math.abs(pitch - deg) < 0.05} className={`sk-pitch${Math.abs(pitch - deg) < 0.05 ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => set({ pitch: deg })} title={tx.t("sketch.roof.pitchChipTitle", { label, deg: Math.round(deg) })}>
                <PitchGlyph deg={deg} />
                {label}
                <small>{tx.t("sketch.roof.degrees", { deg: Math.round(deg) })}</small>
              </button>
            );
          })}
        </div>
        <div className="sk-inline">
          <span className="sk-number-wrap">
            <input className="sk-number" type="number" min={0} max={75} step={0.5} value={Math.round(pitch * 10) / 10} disabled={props.readOnly} aria-label={tx.t("sketch.roof.pitchDegreesLabel")} onChange={(e) => set({ pitch: Math.max(0, Math.min(75, Number(e.target.value) || 0)) })} />
            <span className="sk-unit">°</span>
          </span>
          <span className="sk-caption">{tx.t("sketch.roof.orAnyAngle")}</span>
        </div>
      </div>
      <div className="sk-field">
        <span className="sk-label">{tx.t(face && !slope?.eave ? "sketch.roof.lowestPoint" : "sketch.roof.eaveHeight")}</span>
        <div className="sk-chips">
          {STOREY_CHIPS.map(([storeys, m]) => (
            <button key={storeys} type="button" className={`sk-chip${Math.abs(height - m) < 0.01 ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => set({ eaveHeight: m })}>
              {tx.t("sketch.storeys", { count: storeys })}
            </button>
          ))}
          <span className="sk-number-wrap">
            <input className="sk-number" type="number" min={0} max={150} step={0.1} value={Math.round(height * 10) / 10} disabled={props.readOnly} aria-label={tx.t("sketch.roof.eaveHeightLabel")} onChange={(e) => set({ eaveHeight: Math.max(0, Number(e.target.value) || 0) })} />
            <span className="sk-unit">m</span>
          </span>
        </div>
      </div>
      {face && !slope?.eave && (
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.roof.direction")}</span>
          <div className="sk-chips">
            {[0, 90, 180, 270].map((a) => (
              <button key={a} type="button" className={`sk-chip${Math.round(face.azimuth) === a ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onFace(face.id, { azimuth: a })}>
                {compassName(tx, a)}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** A tiny roof profile at the chip's pitch. */
function PitchGlyph({ deg }: { deg: number }) {
  const t = Math.tan((deg * Math.PI) / 180);
  const h = Math.min(9, 9 * t);
  return (
    <svg width="22" height="11" viewBox="0 0 22 11" aria-hidden>
      {deg < 1 ? <path d="M2 9.5 H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /> : <path d={`M2 10 L11 ${10 - h} L20 10`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />}
    </svg>
  );
}

export function PanelsSection(props: {
  module: S["ModuleSpec"];
  count: number;
  selected: number;
  orientation: Orientation;
  placeMode: PlaceMode;
  faceSelected: boolean;
  readOnly: boolean;
  onOrientation: (o: Orientation) => void;
  onPlaceMode: (m: PlaceMode) => void;
  onFill: () => void;
  onClearFace: () => void;
  onRotate: (deg: number) => void;
  onDelete: () => void;
  onDeselect?: () => void;
  /** Selects every panel (also ⌘/Ctrl A on the map). */
  onSelectAll?: () => void;
  /** The panel tool is active: this is its options section (first in the panel, lit up). */
  highlight?: boolean;
}) {
  const tx = useSketchText();
  return (
    <section className={`sk-side-section sk-panels-section${props.highlight ? " sk-toolsec" : ""}`} aria-label={props.highlight ? tx.t("sketch.toolsec.options", { tool: tx.t("sketch.tool.panel") }) : undefined}>
      <div className="sk-side-head">
        <span className={`sk-side-icon${props.highlight ? " is-tool" : ""}`}><Grid3x3 size={15} /></span>
        <h3>{tx.t(props.highlight ? "sketch.tool.panel" : "sketch.panels.title")}</h3>
        <span className="sk-eyebrow">{props.highlight ? <>{tx.t("sketch.toolsec.tool")} · <kbd className="sk-kbd">P</kbd></> : tx.t("sketch.panels.placed", { count: props.count })}</span>
      </div>
      <div className="sk-module">
        <span className="sk-module-art" aria-hidden />
        <div>
          <b>{props.module.model}</b>
          <span>{tx.t("sketch.panels.module", { watts: tx.num(props.module.watts, 0) })}</span>
        </div>
        <div className="sk-module-count">
          <b>{tx.num((props.count * props.module.watts) / 1000, 2)}</b>
          <span>kW</span>
        </div>
      </div>
      <div className="sk-field">
        <span className="sk-label">{tx.t("sketch.placement.label")}</span>
        <div className="sk-modes" role="radiogroup" aria-label={tx.t("sketch.placement.label")}>
          <button type="button" role="radio" aria-checked={props.placeMode === "magnetic"} className={`sk-mode${props.placeMode === "magnetic" ? " is-active" : ""}`} onClick={() => props.onPlaceMode("magnetic")}>
            <b><Magnet size={15} /> {tx.t("sketch.placement.magnetic")}</b>
            <span>{tx.t("sketch.panels.magneticDetail")}</span>
          </button>
          <button type="button" role="radio" aria-checked={props.placeMode === "free"} className={`sk-mode${props.placeMode === "free" ? " is-active" : ""}`} onClick={() => props.onPlaceMode("free")}>
            <b><Move3d size={15} /> {tx.t("sketch.placement.free")}</b>
            <span>{tx.t("sketch.panels.freeDetail")}</span>
          </button>
        </div>
      </div>
      <div className="sk-field">
        <span className="sk-label">{tx.t("sketch.orientation.label")} <span><kbd className="sk-kbd">O</kbd></span></span>
        <div className="sk-segmented" role="radiogroup" aria-label={tx.t("sketch.orientation.label")} style={{ "--i": props.orientation === "portrait" ? 0 : 1, "--n": 2 } as CSSProperties}>
          {(["portrait", "landscape"] as Orientation[]).map((o) => (
            <button key={o} type="button" role="radio" aria-checked={props.orientation === o} className={props.orientation === o ? "is-active" : ""} onClick={() => props.onOrientation(o)}>
              {o === "portrait" ? <RectangleVertical size={15} /> : <RectangleHorizontal size={15} />}
              {tx.t(o === "portrait" ? "sketch.orientation.portrait" : "sketch.orientation.landscape")}
            </button>
          ))}
        </div>
      </div>
      <div className="sk-actions">
        <button type="button" className="sk-action is-primary is-grow" disabled={props.readOnly || !props.faceSelected} onClick={props.onFill} title={tx.t(props.faceSelected ? "sketch.panels.fillTitle" : "sketch.panels.selectFaceFirst")}>
          <Sparkles size={15} /> {tx.t("sketch.panels.fill")}
        </button>
        <button type="button" className="sk-action" disabled={props.readOnly || !props.faceSelected} onClick={props.onClearFace}>
          {tx.t("sketch.panels.clear")}
        </button>
      </div>
      {props.onSelectAll && props.count > 1 && props.selected < props.count && (
        <button type="button" className="sk-action is-ghost sk-select-all" onClick={props.onSelectAll} title={tx.t("sketch.panels.selectAllTitle")}>
          {tx.t("sketch.panels.selectAll", { count: props.count })} <kbd className="sk-kbd">{KEYS_SELECT_ALL}</kbd>
        </button>
      )}
      {!props.faceSelected && !props.selected && <p className="sk-caption"><Msg id="sketch.panels.howTo" values={{ kbd }} /></p>}
      {props.selected > 0 && (
        <div className="sk-selection-bar">
          <span>{tx.t("sketch.panels.selected", { count: props.selected })}</span>
          <button type="button" className="sk-icon-action" disabled={props.readOnly} onClick={() => props.onRotate(-15)} aria-label={tx.t("sketch.panels.turnAnticlockwise")} title={tx.t("sketch.panels.turnAnticlockwise")}><RotateCcw size={15} /></button>
          <button type="button" className="sk-icon-action" disabled={props.readOnly} onClick={() => props.onRotate(15)} aria-label={tx.t("sketch.panels.turnClockwise")} title={tx.t("sketch.panels.turnClockwise")}><RotateCw size={15} /></button>
          <button type="button" className="sk-icon-action is-danger" disabled={props.readOnly} onClick={props.onDelete} aria-label={tx.t("sketch.panels.deleteSelected")} title={tx.t("sketch.common.deleteKey")}><Trash2 size={15} /></button>
          {props.onDeselect && <button type="button" className="sk-icon-action" onClick={props.onDeselect} aria-label={tx.t("sketch.common.clearSelection")} title={tx.t("sketch.common.clearSelectionKey")}><X size={15} /></button>}
        </div>
      )}
    </section>
  );
}

/** "Design with AI": the upgrade from a Sketch to an automatic design (a new option of the customer). */
export function AiDesignCard({ balance, cost, busy, onStart, error }: { balance: number | null; cost: number; busy: boolean; onStart: (anchor: HTMLElement) => void; error: string | null }) {
  const tx = useSketchText();
  const none = balance != null && balance <= 0;
  return (
    <section className="sk-side-section sk-ai">
      <div className="sk-ai-card">
        <div className="sk-ai-inner">
          <div className="sk-ai-top">
            <span className="sk-ai-mark"><Sparkles size={20} /></span>
            <div>
              <h3>{tx.t("sketch.action.ai")}</h3>
              <p>{tx.t("sketch.ai.ready")}</p>
            </div>
          </div>
          <ul className="sk-ai-list">
            <li><Check size={15} /> {tx.t("sketch.ai.lidar")}</li>
            <li><Check size={15} /> {tx.t("sketch.ai.obstacles")}</li>
            <li><Check size={15} /> {tx.t("sketch.ai.panelsKept")}</li>
          </ul>
          {/* out of Lux it still opens the confirmation: Buy Lux saves the Sketch and comes back here */}
          <button type="button" className="sk-ai-button" onClick={(e) => onStart(e.currentTarget)} disabled={busy}>
            {busy ? <span className="sk-ai-spinner" aria-hidden /> : <Sparkles size={16} />}
            {tx.t(busy ? "sketch.ai.starting" : "sketch.action.ai")}
            <span className="sk-ai-cost">{tx.t("sketch.assist.price", { lux: tx.lux(cost) })}</span>
          </button>
          <p className="sk-ai-note">
            {none ? tx.t("sketch.ai.noteNone") : balance != null ? tx.t("sketch.ai.noteBalance", { lux: tx.lux(balance) }) : tx.t("sketch.ai.note")}
          </p>
          {error && <p className="sk-error" style={{ margin: 0 }}>{error}</p>}
        </div>
      </div>
    </section>
  );
}

/** Shading: per-face solar access, the months, the heatmap switch and the sun time of the shadows. */
export function ShadingSection(props: {
  shading: S["SketchShading"] | null | undefined;
  faces: { id: string; index: number; label: string; access: number | null }[];
  heat: boolean;
  onHeat: (on: boolean) => void;
  sun: SunTime;
  onSun: (s: SunTime) => void;
  drawn: number;
  pricing: boolean;
}) {
  const tx = useSketchText();
  const months = props.shading?.monthly_solar_access ?? null;
  const counts = props.shading?.counts ?? {};
  const horizon = props.shading && !props.shading.horizon.provisional ? "yes" : "no";
  const horizonDeg = tx.num(Math.round(props.shading?.horizon.max_elevation_deg ?? 0), 0);
  return (
    <section className="sk-side-section sk-shading">
      <div className="sk-side-head">
        <span className="sk-side-icon is-sun"><SunDim size={16} /></span>
        <h3>{tx.t("sketch.shading.title")}</h3>
        <span className="sk-eyebrow">{tx.t("sketch.shading.drawn", { count: props.drawn })}</span>
      </div>
      <button type="button" className={`sk-heat-toggle${props.heat ? " is-on" : ""}`} role="switch" aria-checked={props.heat} onClick={() => props.onHeat(!props.heat)}>
        <span className="sk-heat-swatch" aria-hidden style={{ background: `linear-gradient(90deg, ${HEAT_STOPS.map(([, c]) => c).join(", ")})` }} />
        <span>
          <b>{tx.t("sketch.shading.heatmap")}</b>
          <small>{tx.t("sketch.shading.heatmapDetail")}</small>
        </span>
        <kbd className="sk-kbd">A</kbd>
        <i className="sk-switch" aria-hidden />
      </button>
      {props.heat && (
        <div className="sk-heat-legend" aria-label={tx.t("sketch.shading.legend")}>
          <i style={{ background: `linear-gradient(90deg, ${HEAT_STOPS.map(([v, c]) => `${c} ${((v - 0.5) / 0.5) * 100}%`).join(", ")})` }} />
          <span>{tx.t("sketch.shading.atMost", { pct: tx.pct(50) })}</span>
          <span>{tx.pct(75)}</span>
          <span>{tx.pct(100)}</span>
        </div>
      )}
      {props.faces.length > 0 && (
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.shading.byFace")} {props.pricing && <span className="sk-busy">{tx.t("sketch.common.updating")}</span>}</span>
          <ul className="sk-access-list">
            {props.faces.map((f) => (
              <li key={f.id}>
                <span className="sk-face-dot" style={{ "--sk-face-hue": FACE_HUE[f.index % FACE_HUE.length] } as CSSProperties}>{f.index + 1}</span>
                <span className="sk-access-name">{f.label}</span>
                <span className="sk-access-bar"><i style={{ width: `${Math.round((f.access ?? 0) * 100)}%`, background: f.access != null ? heatColor(f.access) : undefined }} /></span>
                <b>{f.access != null ? tx.pct(Math.round(f.access * 100)) : "—"}</b>
              </li>
            ))}
          </ul>
        </div>
      )}
      {months && months.length === 12 && (
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.shading.year")}</span>
          <div className="sk-months" role="img" aria-label={tx.t("sketch.shading.byMonth", { months: tx.list(months.map((m, i) => tx.t("sketch.shading.monthValue", { month: monthName(tx, i), pct: tx.pct(Math.round(m * 100)) }))) })}>
            {months.map((m, i) => (
              <span key={i} className={i === props.sun.month ? "is-current" : undefined} title={tx.t("sketch.shading.monthValue", { month: monthName(tx, i), pct: tx.pct(Math.round(m * 100)) })}>
                <i style={{ height: `${Math.max(4, ((m - 0.4) / 0.6) * 100)}%`, background: heatColor(m) }} />
                <small>{monthName(tx, i, "narrow")}</small>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="sk-field">
        <span className="sk-label">
          {tx.t("sketch.shading.shadows")} <span>{tx.t("sketch.shading.sunTime", { day: tx.date(new Date(2026, props.sun.month, 21), { month: "short", day: "numeric" }), time: tx.date(new Date(2026, props.sun.month, 21, Math.floor(props.sun.hour), props.sun.hour % 1 ? 30 : 0), { hour: "2-digit", minute: "2-digit" }) })}</span>
        </span>
        <div className="sk-sun-time">
          <input type="range" min={0} max={11} step={1} value={props.sun.month} aria-label={tx.t("sketch.shading.monthLabel")} aria-valuetext={monthName(tx, props.sun.month)} onChange={(e) => props.onSun({ ...props.sun, month: Number(e.target.value) })} />
          <input type="range" min={6} max={18} step={0.5} value={props.sun.hour} aria-label={tx.t("sketch.shading.hourLabel")} onChange={(e) => props.onSun({ ...props.sun, hour: Number(e.target.value) })} />
        </div>
      </div>
      {props.shading ? (
        <p className="sk-caption">
          {props.shading.drawn && drawnSources(tx, counts).length
            ? tx.t("sketch.shading.from", { sources: tx.list(drawnSources(tx, counts)), horizon, deg: horizonDeg })
            : tx.t("sketch.shading.roofOnly", { horizon, deg: horizonDeg })}
        </p>
      ) : (
        <p className="sk-caption"><Msg id="sketch.shading.hint" values={{ kbd }} /></p>
      )}
    </section>
  );
}

const WORDS: Record<string, MessageKey> = { tree: "sketch.shading.trees", obstacle: "sketch.shading.obstacles", building: "sketch.shading.buildings", wall: "sketch.shading.walls" };
/** "2 trees", "1 wall"…: what was drawn that shades the roof. */
function drawnSources(tx: Text, counts: Record<string, number>): string[] {
  return Object.entries(counts).filter(([k, n]) => n && WORDS[k]).map(([k, n]) => tx.t(WORDS[k]!, { count: n }));
}

/** Height presets: [label, metres, label values]. */
const HEIGHT_CHIPS: Record<string, [MessageKey, number, Record<string, number>?][]> = {
  tree: [["sketch.height.shrub", 4], ["sketch.height.small", 7], ["sketch.height.medium", 12], ["sketch.height.tall", 18], ["sketch.height.veryTall", 25]],
  building: [["sketch.storeys", 3.5, { count: 1 }], ["sketch.storeys", 6.5, { count: 2 }], ["sketch.storeys", 9.5, { count: 3 }], ["sketch.storeys", 12.5, { count: 4 }]],
  wall: [["sketch.height.fence", 1.8], ["sketch.neighbour.wall", 2.5], ["sketch.height.high", 4]],
  obstacle: [["sketch.pitch.flat", 0.1], ["sketch.obstacle.vent", 0.3], ["sketch.height.low", 0.6], ["sketch.obstacle.chimney", 1.2], ["sketch.height.tall", 2]],
};

/** The selected tree, roof obstacle or neighbouring building or wall: its height, size and kind. */
export function ShadeObjectSection(props: {
  item: ShadeItem | null;
  obstacle: ObstacleItem | null;
  readOnly: boolean;
  onShade: (item: ShadeItem) => void;
  onObstacle: (o: ObstacleItem) => void;
  onDelete: () => void;
  onDeselect: () => void;
}) {
  const tx = useSketchText();
  const t = props.item;
  const o = props.obstacle;
  if (!t && !o) return null;
  const kind = t ? t.kind : "obstacle";
  const height = t ? t.height : o!.height_m ?? 0;
  const setHeight = (h: number) => (t ? props.onShade({ ...t, height: Math.max(0.5, Math.min(120, h)), crownBase: t.crownBase == null ? null : Math.min(t.crownBase, Math.max(0.5, h) - 0.5) }) : props.onObstacle({ ...o!, height_m: Math.max(0, Math.min(10, h)) }));
  const icon = t?.kind === "tree" ? (t.crown === "conical" ? <TreePine size={16} /> : <TreeDeciduous size={16} />) : t?.kind === "building" ? <Building2 size={16} /> : t?.kind === "wall" ? <BrickWall size={16} /> : o && shapeOf(o) === "cylinder" ? <Cylinder size={16} /> : <Box size={16} />;
  const preset = o ? OBSTACLE_PRESETS[o.kind as keyof typeof OBSTACLE_PRESETS] : undefined;
  const title = tx.t(t ? (t.kind === "tree" ? "sketch.shade.tree" : t.kind === "building" ? "sketch.shade.building" : "sketch.neighbour.wall") : preset?.label ?? "sketch.shade.obstacle");
  return (
    <section className="sk-side-section sk-shade-object" key={t?.key ?? o!.key}>
      <div className="sk-side-head">
        <span className={`sk-side-icon is-${kind}`}>{icon}</span>
        <h3>{title}</h3>
        <button type="button" className="sk-icon-action" onClick={props.onDeselect} aria-label={tx.t("sketch.common.clearSelection")} title={tx.t("sketch.common.clearSelectionKey")}><X size={15} /></button>
      </div>
      {o && <p className="sk-caption">{tx.t("sketch.shade.obstacleCaption")}</p>}
      <div className="sk-field">
        <span className="sk-label">{tx.t(o ? "sketch.shade.heightAboveRoof" : "sketch.toolsec.height")} <span>{tx.t("sketch.shade.dragKnob")}</span></span>
        <div className="sk-chips">
          {HEIGHT_CHIPS[kind]!.map(([label, h, values]) => (
            <button key={h} type="button" className={`sk-chip${Math.abs(height - h) < 0.05 ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => setHeight(h)}>{tx.t(label, values)}</button>
          ))}
          <span className="sk-number-wrap">
            <input className="sk-number" type="number" min={0} max={o ? 10 : 120} step={o ? 0.1 : 0.5} value={Math.round(height * 10) / 10} disabled={props.readOnly} aria-label={tx.t("sketch.toolsec.heightLabel")} onChange={(e) => setHeight(Number(e.target.value) || 0)} />
            <span className="sk-unit">m</span>
          </span>
        </div>
      </div>
      {t?.kind === "tree" && (
        <>
          <div className="sk-field">
            <span className="sk-label">{tx.t("sketch.shade.crown")} <span>{tx.t("sketch.shade.crownDetail", { size: tx.num((t.radius ?? 3) * 2, 1), base: tx.num(crownBase(t), 1) })}</span></span>
            <div className="sk-inline">
              <span className="sk-number-wrap">
                <input className="sk-number" type="number" min={0.4} max={60} step={0.5} value={Math.round((t.radius ?? 3) * 20) / 10} disabled={props.readOnly} aria-label={tx.t("sketch.toolsec.crownDiameterLabel")} onChange={(e) => props.onShade({ ...t, radius: Math.max(0.2, Math.min(30, (Number(e.target.value) || 0) / 2)) })} />
                <span className="sk-unit">{tx.t("sketch.toolsec.unitWide")}</span>
              </span>
              <span className="sk-number-wrap">
                <input className="sk-number" type="number" min={0} max={t.height - 0.5} step={0.5} value={Math.round(crownBase(t) * 10) / 10} disabled={props.readOnly} aria-label={tx.t("sketch.shade.crownBaseLabel")} onChange={(e) => props.onShade({ ...t, crownBase: Math.max(0, Math.min(t.height - 0.5, Number(e.target.value) || 0)) })} />
                <span className="sk-unit">{tx.t("sketch.shade.unitUp")}</span>
              </span>
            </div>
          </div>
          <div className="sk-field">
            <span className="sk-label">{tx.t("sketch.shade.shapeLeaves")}</span>
            <div className="sk-chips">
              <button type="button" className={`sk-chip${t.crown === "round" ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onShade({ ...t, crown: "round" })}><TreeDeciduous size={14} /> {tx.t("sketch.shade.round")}</button>
              <button type="button" className={`sk-chip${t.crown === "conical" ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onShade({ ...t, crown: "conical" })}><TreePine size={14} /> {tx.t("sketch.shade.conical")}</button>
              <button type="button" className={`sk-chip${t.leaf === "deciduous" ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onShade({ ...t, leaf: "deciduous" })}><Leaf size={14} /> {tx.t("sketch.tree.deciduous")}</button>
              <button type="button" className={`sk-chip${t.leaf === "evergreen" ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onShade({ ...t, leaf: "evergreen" })}><TreePine size={14} /> {tx.t("sketch.tree.evergreen")}</button>
            </div>
            <span className="sk-caption">{t.leaf === "deciduous" ? tx.t("sketch.shade.deciduousCaption", { winter: tx.pct(60), summer: tx.pct(20) }) : tx.t("sketch.shade.evergreenCaption", { pct: tx.pct(15) })}</span>
          </div>
        </>
      )}
      {o && (
        <div className="sk-field">
          <span className="sk-label">{tx.t("sketch.toolsec.kind")}</span>
          <div className="sk-chips">
            {(Object.keys(OBSTACLE_PRESETS) as (keyof typeof OBSTACLE_PRESETS)[]).map((k) => (
              <button key={k} type="button" className={`sk-chip${o.kind === k ? " is-active" : ""}`} disabled={props.readOnly} onClick={() => props.onObstacle({ ...o, kind: k })}>{tx.t(OBSTACLE_PRESETS[k].label)}</button>
            ))}
          </div>
        </div>
      )}
      <div className="sk-actions">
        <button type="button" className="sk-action is-danger" disabled={props.readOnly} onClick={props.onDelete}><Trash2 size={14} /> {tx.t("sketch.common.delete")}</button>
      </div>
    </section>
  );
}
